import type {
  PendingSettlement,
  PendingSettlementsListData,
} from '../../utils/client-reports/types';
import {
  EmptySection,
  ReportTable,
  SectionHeader,
  StatusText,
  type ReportColumn,
} from './ReportBlocks';
import { titleCase } from './reportLayout';
import { useReportFormat } from './useReportFormat';

// Embarques sin liquidar en una tabla: las vencidas (críticas primero, por
// antigüedad) o las que vencen en los próximos 7 días.
const PendingSettlementsTableView = ({
  number: sectionNumber,
  data,
  variant,
}: {
  number: number;
  data: PendingSettlementsListData;
  variant: 'overdue' | 'dueSoon';
}) => {
  const { t, number, date, plural } = useReportFormat();
  // Detalle por contenedor: va después del resumen por nave.
  const title =
    variant === 'overdue'
      ? t('Detalle por contenedor: críticas y vencidas')
      : t('Detalle por contenedor: por vencer en 7 días');

  if (data.rows.length === 0) {
    return (
      <section className='flex flex-col gap-6'>
        <SectionHeader number={sectionNumber} title={title} />
        <EmptySection>
          {variant === 'overdue'
            ? t('No hay liquidaciones vencidas.')
            : t('No hay liquidaciones que venzan en los próximos 7 días.')}
        </EmptySection>
      </section>
    );
  }

  // Lo que el cliente necesita para actuar va primero (contenedor, atraso y
  // cajas): en celular la tabla se desplaza y las últimas columnas quedan
  // fuera de la pantalla.
  const identifier: ReportColumn<PendingSettlement> = {
    key: 'identifier',
    label: t('Contenedor / AWB'),
    nowrap: true,
    render: (row) => (
      <span className='font-medium'>
        {row.identifiers.join(', ') || row.reference}
      </span>
    ),
  };
  const timing: ReportColumn<PendingSettlement> =
    variant === 'overdue'
      ? {
          key: 'days',
          label: t('Días desde ETA'),
          align: 'right',
          render: (row) => number(row.daysSinceEta),
        }
      : {
          key: 'dueDate',
          label: t('Vence'),
          nowrap: true,
          render: (row) => (
            <span className='font-medium'>{date(row.dueDate)}</span>
          ),
        };
  const ceq: ReportColumn<PendingSettlement> = {
    key: 'ceq',
    label: t('Cajas eq.'),
    align: 'right',
    render: (row) => number(row.ceq),
  };
  // Todas las de "por vencer" están en el mismo estado: no lleva columna.
  const status: ReportColumn<PendingSettlement>[] =
    variant === 'overdue'
      ? [
          {
            key: 'status',
            label: t('Estado'),
            render: (row) =>
              row.status === 'critical' ? (
                <StatusText tone='danger'>{t('Crítica')}</StatusText>
              ) : (
                <StatusText tone='warning'>{t('Vencida')}</StatusText>
              ),
          },
        ]
      : [];
  const hasDestination = data.rows.some((row) => row.destination);
  const allColumns: ReportColumn<PendingSettlement>[] = [
    identifier,
    timing,
    ceq,
    ...status,
    {
      key: 'eta',
      label: 'ETA',
      nowrap: true,
      render: (row) => date(row.eta),
    },
    {
      key: 'vessel',
      label: t('Nave / vuelo'),
      render: (row) => row.vessel || '—',
    },
    {
      key: 'destination',
      label: t('Puerto de destino'),
      render: (row) => (row.destination ? titleCase(row.destination) : '—'),
    },
  ];
  // Si ningún embarque trae puerto, la columna no se muestra.
  const columns = hasDestination
    ? allColumns
    : allColumns.filter((column) => column.key !== 'destination');

  return (
    <section className='flex flex-col gap-8'>
      <SectionHeader
        number={sectionNumber}
        title={title}
        aside={`${plural(data.totals.shipments, '{{n}} embarque', '{{n}} embarques')} · ${t(
          '{{ceq}} cajas eq.',
          { ceq: number(data.totals.ceq) }
        )}`}
      />
      <ReportTable<PendingSettlement>
        label={title}
        rows={data.rows}
        rowKey={(row) => row.reference}
        columns={columns}
        total={{
          identifier: `${t('Total')} · ${plural(
            data.totals.shipments,
            '{{n}} embarque',
            '{{n}} embarques'
          )}`,
          ceq: number(data.totals.ceq),
        }}
      />
    </section>
  );
};

export default PendingSettlementsTableView;
